/**
 * Redux Duck: User Generated Content Management (Stream Feeds)
 *
 * Manages user-generated content (posts, reactions, etc.) using Stream Feeds API
 * Stream Feeds is the source of truth for all post data
 */

import { storableError } from '../util/errors';
import * as log from '../util/log';
import * as streamFeeds from '../util/streamFeeds';
import { transformStreamActivitiesToPosts } from '../util/userGeneratedContentData';
import * as moderationPrefs from '../util/userModerationPrefs';

// ================ Action types ================ //

export const FETCH_CREW_CONTENT_REQUEST = 'app/userGeneratedContent/FETCH_CREW_CONTENT_REQUEST';
export const FETCH_CREW_CONTENT_SUCCESS = 'app/userGeneratedContent/FETCH_CREW_CONTENT_SUCCESS';
export const FETCH_CREW_CONTENT_ERROR = 'app/userGeneratedContent/FETCH_CREW_CONTENT_ERROR';
export const CLEAR_CREW_CONTENT = 'app/userGeneratedContent/CLEAR_CREW_CONTENT';

export const FETCH_USER_CONTENT_REQUEST = 'app/userGeneratedContent/FETCH_USER_CONTENT_REQUEST';
export const FETCH_USER_CONTENT_SUCCESS = 'app/userGeneratedContent/FETCH_USER_CONTENT_SUCCESS';
export const FETCH_USER_CONTENT_ERROR = 'app/userGeneratedContent/FETCH_USER_CONTENT_ERROR';

export const FETCH_ALL_CONTENT_REQUEST = 'app/userGeneratedContent/FETCH_ALL_CONTENT_REQUEST';
export const FETCH_ALL_CONTENT_SUCCESS = 'app/userGeneratedContent/FETCH_ALL_CONTENT_SUCCESS';
export const FETCH_ALL_CONTENT_ERROR = 'app/userGeneratedContent/FETCH_ALL_CONTENT_ERROR';

export const CREATE_CONTENT_REQUEST = 'app/userGeneratedContent/CREATE_CONTENT_REQUEST';
export const CREATE_CONTENT_SUCCESS = 'app/userGeneratedContent/CREATE_CONTENT_SUCCESS';
export const CREATE_CONTENT_ERROR = 'app/userGeneratedContent/CREATE_CONTENT_ERROR';

export const ADD_REACTION_REQUEST = 'app/userGeneratedContent/ADD_REACTION_REQUEST';
export const ADD_REACTION_SUCCESS = 'app/userGeneratedContent/ADD_REACTION_SUCCESS';
export const ADD_REACTION_ERROR = 'app/userGeneratedContent/ADD_REACTION_ERROR';

export const REMOVE_REACTION_REQUEST = 'app/userGeneratedContent/REMOVE_REACTION_REQUEST';
export const REMOVE_REACTION_SUCCESS = 'app/userGeneratedContent/REMOVE_REACTION_SUCCESS';
export const REMOVE_REACTION_ERROR = 'app/userGeneratedContent/REMOVE_REACTION_ERROR';

export const INIT_STREAM_FEEDS_REQUEST = 'app/userGeneratedContent/INIT_STREAM_FEEDS_REQUEST';
export const INIT_STREAM_FEEDS_SUCCESS = 'app/userGeneratedContent/INIT_STREAM_FEEDS_SUCCESS';
export const INIT_STREAM_FEEDS_ERROR = 'app/userGeneratedContent/INIT_STREAM_FEEDS_ERROR';

// New interaction action types
export const REPOST_REQUEST = 'app/userGeneratedContent/REPOST_REQUEST';
export const REPOST_SUCCESS = 'app/userGeneratedContent/REPOST_SUCCESS';
export const REPOST_ERROR = 'app/userGeneratedContent/REPOST_ERROR';

export const HIDE_POST = 'app/userGeneratedContent/HIDE_POST';
export const MUTE_USER = 'app/userGeneratedContent/MUTE_USER';
export const UNHIDE_POST = 'app/userGeneratedContent/UNHIDE_POST';
export const UNMUTE_USER = 'app/userGeneratedContent/UNMUTE_USER';
export const LOAD_MODERATION_PREFERENCES_REQUEST =
  'app/userGeneratedContent/LOAD_MODERATION_PREFERENCES_REQUEST';
export const LOAD_MODERATION_PREFERENCES_SUCCESS =
  'app/userGeneratedContent/LOAD_MODERATION_PREFERENCES_SUCCESS';
export const LOAD_MODERATION_PREFERENCES_ERROR =
  'app/userGeneratedContent/LOAD_MODERATION_PREFERENCES_ERROR';

export const BLOCK_USER_REQUEST = 'app/userGeneratedContent/BLOCK_USER_REQUEST';
export const BLOCK_USER_SUCCESS = 'app/userGeneratedContent/BLOCK_USER_SUCCESS';
export const BLOCK_USER_ERROR = 'app/userGeneratedContent/BLOCK_USER_ERROR';
export const UNBLOCK_USER_REQUEST = 'app/userGeneratedContent/UNBLOCK_USER_REQUEST';
export const UNBLOCK_USER_SUCCESS = 'app/userGeneratedContent/UNBLOCK_USER_SUCCESS';
export const UNBLOCK_USER_ERROR = 'app/userGeneratedContent/UNBLOCK_USER_ERROR';

export const REPORT_POST_REQUEST = 'app/userGeneratedContent/REPORT_POST_REQUEST';
export const REPORT_POST_SUCCESS = 'app/userGeneratedContent/REPORT_POST_SUCCESS';
export const REPORT_POST_ERROR = 'app/userGeneratedContent/REPORT_POST_ERROR';

export const DELETE_POST_REQUEST = 'app/userGeneratedContent/DELETE_POST_REQUEST';
export const DELETE_POST_SUCCESS = 'app/userGeneratedContent/DELETE_POST_SUCCESS';
export const DELETE_POST_ERROR = 'app/userGeneratedContent/DELETE_POST_ERROR';

export const FETCH_NOTIFICATIONS_REQUEST = 'app/userGeneratedContent/FETCH_NOTIFICATIONS_REQUEST';
export const FETCH_NOTIFICATIONS_SUCCESS = 'app/userGeneratedContent/FETCH_NOTIFICATIONS_SUCCESS';
export const FETCH_NOTIFICATIONS_ERROR = 'app/userGeneratedContent/FETCH_NOTIFICATIONS_ERROR';

export const MARK_NOTIFICATIONS_SEEN_REQUEST =
  'app/userGeneratedContent/MARK_NOTIFICATIONS_SEEN_REQUEST';
export const MARK_NOTIFICATIONS_SEEN_SUCCESS =
  'app/userGeneratedContent/MARK_NOTIFICATIONS_SEEN_SUCCESS';
export const MARK_NOTIFICATIONS_SEEN_ERROR =
  'app/userGeneratedContent/MARK_NOTIFICATIONS_SEEN_ERROR';

// ================ Reducer ================ //

const initialState = {
  // Crew content (posts for specific crews)
  crewContent: [],
  isFetchingCrewContent: false,
  fetchCrewContentError: null,

  // User content (all content by current user)
  userContent: [],
  isFetchingUserContent: false,
  fetchUserContentError: null,

  // All content (posts from all crews user follows)
  allContent: [],
  isFetchingAllContent: false,
  fetchAllContentError: null,

  // Content creation
  isCreatingContent: false,
  createContentError: null,

  // Reactions
  isAddingReaction: false,
  addReactionError: null,

  // Notifications
  notifications: [],
  unreadCount: 0,
  unseenCount: 0,
  isFetchingNotifications: false,
  fetchNotificationsError: null,
  isMarkingSeen: false,
  isRemovingReaction: false,
  removeReactionError: null,

  // Repost
  isReposting: false,
  repostError: null,

  // Moderation
  isLoadingModerationPreferences: false,
  loadModerationPreferencesError: null,
  isBlockingUser: false,
  blockUserError: null,
  isUnblockingUser: false,
  unblockUserError: null,
  isReportingPost: false,
  reportPostError: null,
  isDeletingPost: false,
  deletePostError: null,

  // Hidden posts / muted users (IDs, synced from localStorage)
  hiddenPostIds: [...moderationPrefs.getHiddenPosts()],
  mutedUserIds: [...moderationPrefs.getMutedUsers()],
  blockedUserIds: [],

  // Stream Feeds initialization
  streamFeedsInitialized: false,
  isInitializingStreamFeeds: false,
  initStreamFeedsError: null,
};

const uniqueIds = ids => [...new Set((ids || []).map(id => String(id)))];

const flattenNotificationActivities = item => {
  if (!item) return [];
  return Array.isArray(item.activities) && item.activities.length > 0 ? item.activities : [item];
};

const isTaggedNotificationItem = item => {
  const activities = flattenNotificationActivities(item);
  return activities.length > 0 && activities.every(activity => activity?.verb === 'tagged');
};

const isUnseenNotificationItem = item => {
  if (!item) return false;

  if (typeof item.is_seen === 'boolean') {
    return item.is_seen === false;
  }

  if (typeof item.seen === 'boolean') {
    return item.seen === false;
  }

  const activities = flattenNotificationActivities(item);

  return activities.some(activity => {
    if (typeof activity?.is_seen === 'boolean') {
      return activity.is_seen === false;
    }

    if (typeof activity?.seen === 'boolean') {
      return activity.seen === false;
    }

    return true;
  });
};

const countUnseenNotificationsExcludingTagged = activities =>
  (activities || []).filter(
    item => !isTaggedNotificationItem(item) && isUnseenNotificationItem(item)
  ).length;

const filterPostsForViewer = (
  posts,
  hiddenPostIds = [],
  mutedUserIds = [],
  blockedUserIds = []
) => {
  const hidden = new Set(uniqueIds(hiddenPostIds));
  const hiddenAuthors = new Set([...uniqueIds(mutedUserIds), ...uniqueIds(blockedUserIds)]);

  return (posts || []).filter(post => {
    if (hidden.has(String(post.id))) return false;
    const authorId = post.authorId || post.user_id || post.actor?.id;
    if (authorId && hiddenAuthors.has(String(authorId))) return false;
    return true;
  });
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    // Stream Feeds initialization
    case INIT_STREAM_FEEDS_REQUEST:
      return {
        ...state,
        isInitializingStreamFeeds: true,
        initStreamFeedsError: null,
      };
    case INIT_STREAM_FEEDS_SUCCESS:
      return {
        ...state,
        isInitializingStreamFeeds: false,
        streamFeedsInitialized: true,
        initStreamFeedsError: null,
      };
    case INIT_STREAM_FEEDS_ERROR:
      return {
        ...state,
        isInitializingStreamFeeds: false,
        streamFeedsInitialized: false,
        initStreamFeedsError: payload,
      };
    case LOAD_MODERATION_PREFERENCES_REQUEST:
      return {
        ...state,
        isLoadingModerationPreferences: true,
        loadModerationPreferencesError: null,
      };
    case LOAD_MODERATION_PREFERENCES_SUCCESS: {
      const hiddenPostIds = uniqueIds([...state.hiddenPostIds, ...(payload.hiddenPostIds || [])]);
      const mutedUserIds = uniqueIds([...state.mutedUserIds, ...(payload.mutedUserIds || [])]);
      const blockedUserIds = uniqueIds([
        ...(state.blockedUserIds || []),
        ...(payload.blockedUserIds || []),
      ]);

      return {
        ...state,
        isLoadingModerationPreferences: false,
        loadModerationPreferencesError: null,
        hiddenPostIds,
        mutedUserIds,
        blockedUserIds,
        crewContent: filterPostsForViewer(
          state.crewContent,
          hiddenPostIds,
          mutedUserIds,
          blockedUserIds
        ),
        userContent: filterPostsForViewer(
          state.userContent,
          hiddenPostIds,
          mutedUserIds,
          blockedUserIds
        ),
        allContent: filterPostsForViewer(
          state.allContent,
          hiddenPostIds,
          mutedUserIds,
          blockedUserIds
        ),
      };
    }
    case LOAD_MODERATION_PREFERENCES_ERROR:
      return {
        ...state,
        isLoadingModerationPreferences: false,
        loadModerationPreferencesError: payload,
      };

    case CLEAR_CREW_CONTENT:
      return {
        ...state,
        crewContent: [], // Clear all crew content
      };

    // Fetch crew content
    case FETCH_CREW_CONTENT_REQUEST:
      return {
        ...state,
        isFetchingCrewContent: true,
        // Don't clear existing content - we want to accumulate posts from multiple crews
        fetchCrewContentError: null,
      };
    case FETCH_CREW_CONTENT_SUCCESS:
      // Deduplicate posts by ID to prevent duplicates
      const existingPostIds = new Set(state.crewContent.map(post => post.id));
      const newPosts = (payload.content || []).filter(post => !existingPostIds.has(post.id));

      return {
        ...state,
        isFetchingCrewContent: false,
        crewContent: filterPostsForViewer(
          [...state.crewContent, ...newPosts],
          state.hiddenPostIds,
          state.mutedUserIds,
          state.blockedUserIds
        ),
        fetchCrewContentError: null,
      };
    case FETCH_CREW_CONTENT_ERROR:
      return {
        ...state,
        isFetchingCrewContent: false,
        fetchCrewContentError: payload,
      };

    // Fetch user content
    case FETCH_USER_CONTENT_REQUEST:
      return {
        ...state,
        isFetchingUserContent: true,
        fetchUserContentError: null,
      };
    case FETCH_USER_CONTENT_SUCCESS:
      return {
        ...state,
        isFetchingUserContent: false,
        userContent: filterPostsForViewer(
          payload.content || [],
          state.hiddenPostIds,
          state.mutedUserIds,
          state.blockedUserIds
        ),
        fetchUserContentError: null,
      };
    case FETCH_USER_CONTENT_ERROR:
      return {
        ...state,
        isFetchingUserContent: false,
        fetchUserContentError: payload,
      };

    // Fetch all content
    case FETCH_ALL_CONTENT_REQUEST:
      return {
        ...state,
        isFetchingAllContent: true,
        fetchAllContentError: null,
      };
    case FETCH_ALL_CONTENT_SUCCESS:
      return {
        ...state,
        isFetchingAllContent: false,
        allContent: filterPostsForViewer(
          payload.content || [],
          state.hiddenPostIds,
          state.mutedUserIds,
          state.blockedUserIds
        ),
        fetchAllContentError: null,
      };
    case FETCH_ALL_CONTENT_ERROR:
      return {
        ...state,
        isFetchingAllContent: false,
        fetchAllContentError: payload,
      };

    // Create content
    case CREATE_CONTENT_REQUEST:
      return {
        ...state,
        isCreatingContent: true,
        createContentError: null,
      };
    case CREATE_CONTENT_SUCCESS:
      return {
        ...state,
        isCreatingContent: false,
        // Optimistically add to feeds
        crewContent: filterPostsForViewer(
          [payload.content, ...state.crewContent],
          state.hiddenPostIds,
          state.mutedUserIds,
          state.blockedUserIds
        ),
        userContent: filterPostsForViewer(
          [payload.content, ...state.userContent],
          state.hiddenPostIds,
          state.mutedUserIds,
          state.blockedUserIds
        ),
        allContent: filterPostsForViewer(
          [payload.content, ...state.allContent],
          state.hiddenPostIds,
          state.mutedUserIds,
          state.blockedUserIds
        ),
        createContentError: null,
      };
    case CREATE_CONTENT_ERROR:
      return {
        ...state,
        isCreatingContent: false,
        createContentError: payload,
      };

    // Add reaction
    case ADD_REACTION_REQUEST:
      return {
        ...state,
        isAddingReaction: true,
        addReactionError: null,
      };
    case ADD_REACTION_SUCCESS:
      return {
        ...state,
        isAddingReaction: false,
        // Update reaction counts in the activity
        crewContent: updateActivityReaction(
          state.crewContent,
          payload.activityId,
          payload.reactionType,
          'add'
        ),
        userContent: updateActivityReaction(
          state.userContent,
          payload.activityId,
          payload.reactionType,
          'add'
        ),
        allContent: updateActivityReaction(
          state.allContent,
          payload.activityId,
          payload.reactionType,
          'add'
        ),
        addReactionError: null,
      };
    case ADD_REACTION_ERROR:
      return {
        ...state,
        isAddingReaction: false,
        addReactionError: payload,
      };

    // Remove reaction
    case REMOVE_REACTION_REQUEST:
      return {
        ...state,
        isRemovingReaction: true,
        removeReactionError: null,
      };
    case REMOVE_REACTION_SUCCESS:
      return {
        ...state,
        isRemovingReaction: false,
        crewContent: updateActivityReaction(
          state.crewContent,
          payload.activityId,
          payload.reactionType,
          'remove'
        ),
        userContent: updateActivityReaction(
          state.userContent,
          payload.activityId,
          payload.reactionType,
          'remove'
        ),
        allContent: updateActivityReaction(
          state.allContent,
          payload.activityId,
          payload.reactionType,
          'remove'
        ),
        removeReactionError: null,
      };
    case REMOVE_REACTION_ERROR:
      return {
        ...state,
        isRemovingReaction: false,
        removeReactionError: payload,
      };

    // ── Repost ──
    case REPOST_REQUEST:
      return { ...state, isReposting: true, repostError: null };
    case REPOST_SUCCESS:
      return {
        ...state,
        isReposting: false,
        crewContent: [payload.content, ...state.crewContent],
        allContent: [payload.content, ...state.allContent],
      };
    case REPOST_ERROR:
      return { ...state, isReposting: false, repostError: payload };

    // ── Hide post (local) ──
    case HIDE_POST:
      return {
        ...state,
        hiddenPostIds: uniqueIds([...state.hiddenPostIds, payload.postId]),
        crewContent: state.crewContent.filter(p => p.id !== payload.postId),
        allContent: state.allContent.filter(p => p.id !== payload.postId),
        userContent: state.userContent.filter(p => p.id !== payload.postId),
      };
    case UNHIDE_POST:
      return {
        ...state,
        hiddenPostIds: state.hiddenPostIds.filter(id => id !== payload.postId),
      };

    // ── Mute user (local) ──
    case MUTE_USER:
      return {
        ...state,
        mutedUserIds: uniqueIds([...state.mutedUserIds, payload.userId]),
        crewContent: state.crewContent.filter(p => (p.authorId || p.user_id) !== payload.userId),
        allContent: state.allContent.filter(p => (p.authorId || p.user_id) !== payload.userId),
      };
    case UNMUTE_USER:
      return {
        ...state,
        mutedUserIds: state.mutedUserIds.filter(id => id !== payload.userId),
      };

    // ── Block user ──
    case BLOCK_USER_REQUEST:
      return { ...state, isBlockingUser: true, blockUserError: null };
    case BLOCK_USER_SUCCESS:
      return {
        ...state,
        isBlockingUser: false,
        blockedUserIds: uniqueIds([...(state.blockedUserIds || []), payload.targetUserId]),
        crewContent: state.crewContent.filter(
          p => (p.authorId || p.user_id) !== payload.targetUserId
        ),
        allContent: state.allContent.filter(
          p => (p.authorId || p.user_id) !== payload.targetUserId
        ),
      };
    case BLOCK_USER_ERROR:
      return { ...state, isBlockingUser: false, blockUserError: payload };
    case UNBLOCK_USER_REQUEST:
      return { ...state, isUnblockingUser: true, unblockUserError: null };
    case UNBLOCK_USER_SUCCESS: {
      const targetUserId = String(payload.targetUserId);
      return {
        ...state,
        isUnblockingUser: false,
        unblockUserError: null,
        blockedUserIds: (state.blockedUserIds || []).filter(id => id !== targetUserId),
      };
    }
    case UNBLOCK_USER_ERROR:
      return { ...state, isUnblockingUser: false, unblockUserError: payload };

    // ── Report post ──
    case REPORT_POST_REQUEST:
      return { ...state, isReportingPost: true, reportPostError: null };
    case REPORT_POST_SUCCESS:
      return { ...state, isReportingPost: false };
    case REPORT_POST_ERROR:
      return { ...state, isReportingPost: false, reportPostError: payload };

    // ── Delete post ──
    case DELETE_POST_REQUEST:
      return { ...state, isDeletingPost: true, deletePostError: null };
    case DELETE_POST_SUCCESS:
      return {
        ...state,
        isDeletingPost: false,
        crewContent: state.crewContent.filter(p => p.id !== payload.activityId),
        allContent: state.allContent.filter(p => p.id !== payload.activityId),
        userContent: state.userContent.filter(p => p.id !== payload.activityId),
      };
    case DELETE_POST_ERROR:
      return { ...state, deletePostError: payload };

    case FETCH_NOTIFICATIONS_REQUEST:
      return { ...state, isFetchingNotifications: true, fetchNotificationsError: null };
    case FETCH_NOTIFICATIONS_SUCCESS:
      const filteredUnseenCount = countUnseenNotificationsExcludingTagged(payload.activities);
      return {
        ...state,
        isFetchingNotifications: false,
        notifications: payload.activities,
        unreadCount: payload.unread,
        unseenCount: filteredUnseenCount,
      };
    case FETCH_NOTIFICATIONS_ERROR:
      return { ...state, isFetchingNotifications: false, fetchNotificationsError: payload };

    case MARK_NOTIFICATIONS_SEEN_REQUEST:
      return { ...state, isMarkingSeen: true };
    case MARK_NOTIFICATIONS_SEEN_SUCCESS:
      return { ...state, isMarkingSeen: false, unseenCount: 0 };
    case MARK_NOTIFICATIONS_SEEN_ERROR:
      return { ...state, isMarkingSeen: false };

    default:
      return state;
  }
}

// Helper function to update reaction counts
const updateActivityReaction = (activities, activityId, reactionType, operation) => {
  return activities.map(activity => {
    if (activity.id === activityId) {
      const reactionCounts = activity.reaction_counts || {};
      const currentCount = reactionCounts[reactionType] || 0;

      return {
        ...activity,
        reaction_counts: {
          ...reactionCounts,
          [reactionType]: operation === 'add' ? currentCount + 1 : Math.max(0, currentCount - 1),
        },
      };
    }
    return activity;
  });
};

// ================ Action creators ================ //

// Stream Feeds initialization
export const initStreamFeedsRequest = () => ({
  type: INIT_STREAM_FEEDS_REQUEST,
});

export const initStreamFeedsSuccess = () => ({
  type: INIT_STREAM_FEEDS_SUCCESS,
});

export const initStreamFeedsError = error => ({
  type: INIT_STREAM_FEEDS_ERROR,
  payload: error,
});

export const clearCrewContent = () => ({
  type: CLEAR_CREW_CONTENT,
});

// Fetch crew content
export const fetchCrewContentRequest = () => ({
  type: FETCH_CREW_CONTENT_REQUEST,
});

export const fetchCrewContentSuccess = content => ({
  type: FETCH_CREW_CONTENT_SUCCESS,
  payload: { content },
});

export const fetchCrewContentError = error => ({
  type: FETCH_CREW_CONTENT_ERROR,
  payload: error,
});

// Fetch user content
export const fetchUserContentRequest = () => ({
  type: FETCH_USER_CONTENT_REQUEST,
});

export const fetchUserContentSuccess = content => ({
  type: FETCH_USER_CONTENT_SUCCESS,
  payload: { content },
});

export const fetchUserContentError = error => ({
  type: FETCH_USER_CONTENT_ERROR,
  payload: error,
});

// Fetch all content
export const fetchAllContentRequest = () => ({
  type: FETCH_ALL_CONTENT_REQUEST,
});

export const fetchAllContentSuccess = content => ({
  type: FETCH_ALL_CONTENT_SUCCESS,
  payload: { content },
});

export const fetchAllContentError = error => ({
  type: FETCH_ALL_CONTENT_ERROR,
  payload: error,
});

// Create content
export const createContentRequest = () => ({
  type: CREATE_CONTENT_REQUEST,
});

export const createContentSuccess = content => ({
  type: CREATE_CONTENT_SUCCESS,
  payload: { content },
});

export const createContentError = error => ({
  type: CREATE_CONTENT_ERROR,
  payload: error,
});

// Add reaction
export const addReactionRequest = () => ({
  type: ADD_REACTION_REQUEST,
});

export const addReactionSuccess = (activityId, reactionType, reaction) => ({
  type: ADD_REACTION_SUCCESS,
  payload: { activityId, reactionType, reaction },
});

export const addReactionError = error => ({
  type: ADD_REACTION_ERROR,
  payload: error,
});

// Remove reaction
export const removeReactionRequest = () => ({
  type: REMOVE_REACTION_REQUEST,
});

export const removeReactionSuccess = (activityId, reactionType) => ({
  type: REMOVE_REACTION_SUCCESS,
  payload: { activityId, reactionType },
});

export const removeReactionError = error => ({
  type: REMOVE_REACTION_ERROR,
  payload: error,
});

// Repost
export const repostRequest = () => ({ type: REPOST_REQUEST });
export const repostSuccess = content => ({ type: REPOST_SUCCESS, payload: { content } });
export const repostError = error => ({ type: REPOST_ERROR, payload: error });

// Hide / Mute (local)
export const hidePostAction = postId => ({ type: HIDE_POST, payload: { postId } });
export const muteUserAction = userId => ({ type: MUTE_USER, payload: { userId } });
export const unhidePostAction = postId => ({ type: UNHIDE_POST, payload: { postId } });
export const unmuteUserAction = userId => ({ type: UNMUTE_USER, payload: { userId } });
export const loadModerationPreferencesRequest = () => ({
  type: LOAD_MODERATION_PREFERENCES_REQUEST,
});
export const loadModerationPreferencesSuccess = preferences => ({
  type: LOAD_MODERATION_PREFERENCES_SUCCESS,
  payload: preferences,
});
export const loadModerationPreferencesError = error => ({
  type: LOAD_MODERATION_PREFERENCES_ERROR,
  payload: error,
});

// Block user
export const blockUserRequest = () => ({ type: BLOCK_USER_REQUEST });
export const blockUserSuccess = targetUserId => ({
  type: BLOCK_USER_SUCCESS,
  payload: { targetUserId },
});
export const blockUserError = error => ({ type: BLOCK_USER_ERROR, payload: error });
export const unblockUserRequest = () => ({ type: UNBLOCK_USER_REQUEST });
export const unblockUserSuccess = targetUserId => ({
  type: UNBLOCK_USER_SUCCESS,
  payload: { targetUserId },
});
export const unblockUserError = error => ({ type: UNBLOCK_USER_ERROR, payload: error });

// Report post
export const reportPostRequest = () => ({ type: REPORT_POST_REQUEST });
export const reportPostSuccess = () => ({ type: REPORT_POST_SUCCESS });
export const reportPostError = error => ({ type: REPORT_POST_ERROR, payload: error });

// Delete post
export const deletePostRequest = () => ({ type: DELETE_POST_REQUEST });
export const deletePostSuccess = activityId => ({
  type: DELETE_POST_SUCCESS,
  payload: { activityId },
});
export const deletePostError = error => ({ type: DELETE_POST_ERROR, payload: error });

// ================ Thunks ================ //

const CREW_CONTENT_CACHE_DURATION = 30 * 1000;
const crewContentCache = new Map();
const crewContentRequests = new Map();

/**
 * Initialize Stream Feeds for the current user
 */
export const initializeStreamFeeds = (userId, userName) => dispatch => {
  dispatch(initStreamFeedsRequest());

  return streamFeeds
    .initializeStreamFeeds(userId, userName)
    .then(() => {
      dispatch(initStreamFeedsSuccess());
      return dispatch(loadModerationPreferences()).then(() => true);
    })
    .catch(e => {
      log.error(e, 'initialize-stream-feeds-failed');
      dispatch(initStreamFeedsError(storableError(e)));
      throw e;
    });
};

export const loadModerationPreferences = () => dispatch => {
  dispatch(loadModerationPreferencesRequest());

  return streamFeeds
    .getModerationPreferences()
    .then(preferences => {
      dispatch(loadModerationPreferencesSuccess(preferences));
      return preferences;
    })
    .catch(e => {
      log.error(e, 'load-moderation-preferences-failed');
      dispatch(loadModerationPreferencesError(storableError(e)));
      throw e;
    });
};

/**
 * Fetch content for a specific crew from Stream Feeds
 */
export const fetchCrewContent = (crewId, options = {}) => dispatch => {
  if (!crewId) {
    return Promise.resolve([]);
  }

  const { limit = 25 } = options;
  const cacheKey = `${String(crewId)}|${String(limit)}`;
  const cachedEntry = crewContentCache.get(cacheKey);

  if (
    cachedEntry &&
    cachedEntry.posts &&
    Date.now() - cachedEntry.timestamp < CREW_CONTENT_CACHE_DURATION
  ) {
    dispatch(fetchCrewContentSuccess(cachedEntry.posts));
    return Promise.resolve(cachedEntry.posts);
  }

  if (crewContentRequests.has(cacheKey)) {
    return crewContentRequests.get(cacheKey);
  }

  dispatch(fetchCrewContentRequest());

  const requestPromise = streamFeeds
    .getCrewPosts(crewId, limit)
    .then(activities => {
      const transformedPosts = transformStreamActivitiesToPosts(activities);
      crewContentCache.set(cacheKey, {
        posts: transformedPosts,
        timestamp: Date.now(),
      });
      dispatch(fetchCrewContentSuccess(transformedPosts));
      return transformedPosts;
    })
    .catch(e => {
      log.error(e, 'fetch-crew-content-failed', { crewId });
      dispatch(fetchCrewContentError(storableError(e)));
      throw e;
    })
    .finally(() => {
      crewContentRequests.delete(cacheKey);
    });

  crewContentRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

/**
 * Fetch content for current user from Stream Feeds
 */
export const fetchUserContent = (userId, options = {}) => dispatch => {
  if (!userId) {
    return Promise.resolve([]);
  }

  dispatch(fetchUserContentRequest());

  const { limit = 25 } = options;

  return streamFeeds
    .getUserPosts(userId, limit)
    .then(activities => {
      dispatch(fetchUserContentSuccess(activities));
      return activities;
    })
    .catch(e => {
      log.error(e, 'fetch-user-content-failed', { userId });
      dispatch(fetchUserContentError(storableError(e)));
      throw e;
    });
};

/**
 * Fetch timeline feed (all crews user follows)
 */
export const fetchAllContent = (options = {}) => (dispatch, getState) => {
  const { currentUser } = getState().user;
  if (!currentUser || !currentUser.id || !currentUser.id.uuid) {
    return Promise.resolve([]);
  }

  dispatch(fetchAllContentRequest());

  const userId = currentUser.id.uuid;
  const { limit = 25 } = options;

  return streamFeeds
    .getTimelineFeed(userId, limit)
    .then(activities => {
      dispatch(fetchAllContentSuccess(activities));
      return activities;
    })
    .catch(e => {
      log.error(e, 'fetch-all-content-failed');
      dispatch(fetchAllContentError(storableError(e)));
      throw e;
    });
};

/**
 * Create new content (post) in Stream Feeds
 */
export const createContent = contentData => (dispatch, getState) => {
  const { currentUser } = getState().user;
  if (!currentUser || !currentUser.id || !currentUser.id.uuid) {
    return Promise.reject(new Error('No authenticated user'));
  }

  dispatch(createContentRequest());
  const userId = currentUser.id.uuid;

  const postData = {
    userId,
    crew_id: contentData.crew_id,
    content: contentData.content,
    image_url: contentData.image_url || null,
    // Pass through all other metadata
    author_handle: contentData.author_handle || null,
    author_name: contentData.author_name,
    crew_name: contentData.crew_name,
    verified: contentData.verified,
    reply_settings: contentData.reply_settings,
    images_count: contentData.images_count,
    all_images: contentData.all_images,
    gif_attachment: contentData.gif_attachment || null,
    location_attachment: contentData.location_attachment || null,
    poll_attachment: contentData.poll_attachment || null,
    mentionedUserIds: contentData.mentionedUserIds || [],
  };

  return streamFeeds
    .createPost(postData)
    .then(activity => {
      const transformedPost = transformStreamActivitiesToPosts([activity])[0] || activity;

      // The create flow redirects straight to CrewPage, which clears Redux
      // content and reads the short-lived crew cache. Keep that cache in sync
      // with the successful write so the new post is visible immediately,
      // rather than requiring a manual reload or waiting for feed eventual
      // consistency.
      const crewId = String(contentData.crew_id || '');
      if (crewId && transformedPost) {
        const matchingKeys = [...crewContentCache.keys()].filter(key =>
          key.startsWith(`${crewId}|`)
        );
        if (matchingKeys.length === 0) matchingKeys.push(`${crewId}|20`);

        matchingKeys.forEach(cacheKey => {
          const cachedEntry = crewContentCache.get(cacheKey);
          const cachedPosts = Array.isArray(cachedEntry?.posts) ? cachedEntry.posts : [];
          const postId = transformedPost.id;
          const withoutDuplicate = postId
            ? cachedPosts.filter(post => String(post.id) !== String(postId))
            : cachedPosts;
          crewContentCache.set(cacheKey, {
            posts: [transformedPost, ...withoutDuplicate],
            timestamp: Date.now(),
          });
        });
      }

      dispatch(createContentSuccess(transformedPost));
      return transformedPost;
    })
    .catch(e => {
      log.error(e, 'create-content-failed', { userId, crewId: contentData.crew_id });
      dispatch(createContentError(storableError(e)));
      throw e;
    });
};

/**
 * Add a reaction (like, love, etc.) to a post
 */
export const addReaction = (
  activityId,
  reactionType = 'like',
  text = '',
  userName = '',
  data = null,
  mentionedUserIds = []
) => dispatch => {
  dispatch(addReactionRequest());

  return streamFeeds
    .addReaction(activityId, reactionType, text, userName, data, mentionedUserIds)
    .then(reaction => {
      dispatch(addReactionSuccess(activityId, reactionType, reaction));
      return reaction;
    })
    .catch(e => {
      log.error(e, 'add-reaction-failed', {
        activityId,
        reactionType,
        text,
        userName,
        data,
        mentionedUserIds,
      });
      dispatch(addReactionError(storableError(e)));
      throw e;
    });
};

/**
 * Remove a reaction from a post
 */
export const removeReaction = (activityId, reactionId, reactionType = 'like') => dispatch => {
  dispatch(removeReactionRequest());

  return streamFeeds
    .removeReaction(reactionId)
    .then(() => {
      dispatch(removeReactionSuccess(activityId, reactionType));
    })
    .catch(e => {
      log.error(e, 'remove-reaction-failed', { reactionId });
      dispatch(removeReactionError(storableError(e)));
      throw e;
    });
};

/**
 * Repost (quote) an activity
 */
export const repostActivity = ({ originalActivityId, quoteText, crewId, originalPost }) => (
  dispatch,
  getState
) => {
  dispatch(repostRequest());

  return streamFeeds
    .createRepost({ originalActivityId, quoteText, crewId, originalPost })
    .then(activity => {
      // Transform the raw Stream activity through the standard transformer
      // so that quoted_post is hydrated into a proper quotedPost object
      const transformedPost = transformStreamActivitiesToPosts([activity])[0] || activity;
      dispatch(repostSuccess(transformedPost));
      return transformedPost;
    })
    .catch(e => {
      log.error(e, 'repost-failed', { originalActivityId });
      dispatch(repostError(storableError(e)));
      throw e;
    });
};

/**
 * Hide a post and persist the preference server-side.
 */
export const hidePost = postId => dispatch => {
  moderationPrefs.hidePost(postId);
  dispatch(hidePostAction(String(postId)));

  return streamFeeds.hidePostPreference(postId).catch(e => {
    moderationPrefs.unhidePost(postId);
    dispatch(unhidePostAction(String(postId)));
    log.error(e, 'hide-post-failed', { postId });
    throw e;
  });
};

/**
 * Mute a user and persist the preference server-side.
 */
export const muteUser = userId => dispatch => {
  moderationPrefs.muteUser(userId);
  dispatch(muteUserAction(String(userId)));

  return streamFeeds.muteUserPreference(userId).catch(e => {
    moderationPrefs.unmuteUser(userId);
    dispatch(unmuteUserAction(String(userId)));
    log.error(e, 'mute-user-failed', { userId });
    throw e;
  });
};

export const unmuteUser = userId => dispatch => {
  moderationPrefs.unmuteUser(userId);
  dispatch(unmuteUserAction(String(userId)));

  return streamFeeds.unmuteUserPreference(userId).catch(e => {
    moderationPrefs.muteUser(userId);
    dispatch(muteUserAction(String(userId)));
    log.error(e, 'unmute-user-failed', { userId });
    throw e;
  });
};

/**
 * Block a user via Stream API
 */
export const blockUserThunk = targetUserId => dispatch => {
  dispatch(blockUserRequest());

  return streamFeeds
    .blockUser(targetUserId)
    .then(() => {
      dispatch(blockUserSuccess(String(targetUserId)));
    })
    .catch(e => {
      log.error(e, 'block-user-failed', { targetUserId });
      dispatch(blockUserError(storableError(e)));
      throw e;
    });
};

export const unblockUserThunk = targetUserId => dispatch => {
  dispatch(unblockUserRequest());

  return streamFeeds
    .unblockUser(targetUserId)
    .then(() => {
      dispatch(unblockUserSuccess(String(targetUserId)));
    })
    .catch(e => {
      log.error(e, 'unblock-user-failed', { targetUserId });
      dispatch(unblockUserError(storableError(e)));
      throw e;
    });
};

/**
 * Report a post via Stream API
 */
export const reportPost = (payloadOrActivityId, reason = '') => (dispatch, getState) => {
  dispatch(reportPostRequest());

  const payload =
    typeof payloadOrActivityId === 'object' && payloadOrActivityId !== null
      ? payloadOrActivityId
      : { activityId: payloadOrActivityId, reason };

  const { activityId, targetUserId = null, details = {}, reason: reportReason = reason } = payload;
  const currentUser = getState()?.user?.currentUser;
  const profile = currentUser?.attributes?.profile || {};
  const reporterDisplayName =
    profile.displayName ||
    [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
    profile.publicData?.handle ||
    currentUser?.id?.uuid ||
    'Unknown reporter';
  const enrichedDetails = {
    ...details,
    reporterDisplayName,
    reporterHandle: details.reporterHandle || profile.publicData?.handle || null,
    reporterUserId: details.reporterUserId || currentUser?.id?.uuid || null,
  };

  return streamFeeds
    .reportActivity(activityId, reportReason, targetUserId, enrichedDetails)
    .then(() => {
      dispatch(reportPostSuccess());
    })
    .catch(e => {
      log.error(e, 'report-post-failed', { activityId });
      dispatch(reportPostError(storableError(e)));
      throw e;
    });
};

/**
 * Delete a post (author only)
 */
export const deletePost = (activityId, userId, options = {}) => dispatch => {
  dispatch(deletePostRequest());

  return streamFeeds
    .deleteActivity(activityId, userId, options)
    .then(() => {
      dispatch(deletePostSuccess(activityId));
    })
    .catch(e => {
      log.error(e, 'delete-post-failed', { activityId });
      dispatch(deletePostError(storableError(e)));
      throw e;
    });
};

/**
 * Follow a crew (adds crew feed to user's timeline)
 */
export const followCrew = crewId => (dispatch, getState) => {
  const { currentUser } = getState().user;
  if (!currentUser || !currentUser.id || !currentUser.id.uuid) {
    return Promise.reject(new Error('No authenticated user'));
  }

  const userId = currentUser.id.uuid;

  return streamFeeds
    .followCrew(userId, crewId)
    .then(() => {
      // Optionally refetch timeline
      dispatch(fetchAllContent());
    })
    .catch(e => {
      log.error(e, 'follow-crew-failed', { userId, crewId });
      throw e;
    });
};

/**
 * Unfollow a crew
 */
export const unfollowCrew = crewId => (dispatch, getState) => {
  const { currentUser } = getState().user;
  if (!currentUser || !currentUser.id || !currentUser.id.uuid) {
    return Promise.reject(new Error('No authenticated user'));
  }

  const userId = currentUser.id.uuid;

  return streamFeeds
    .unfollowCrew(userId, crewId)
    .then(() => {
      // Optionally refetch timeline
      dispatch(fetchAllContent());
    })
    .catch(e => {
      log.error(e, 'unfollow-crew-failed', { userId, crewId });
      throw e;
    });
};

/**
 * Notifications
 */
export const fetchNotifications = (userId, options = {}) => async dispatch => {
  dispatch({ type: FETCH_NOTIFICATIONS_REQUEST });
  try {
    const response = await streamFeeds.getUserNotifications(userId, options);
    dispatch({ type: FETCH_NOTIFICATIONS_SUCCESS, payload: response });
    return response;
  } catch (error) {
    const err = storableError(error);
    dispatch({ type: FETCH_NOTIFICATIONS_ERROR, payload: err });
    throw err;
  }
};

export const markNotificationsSeen = userId => async dispatch => {
  dispatch({ type: MARK_NOTIFICATIONS_SEEN_REQUEST });
  try {
    const response = await streamFeeds.getUserNotifications(userId, { markSeen: true });
    dispatch({ type: MARK_NOTIFICATIONS_SEEN_SUCCESS });
    return response;
  } catch (error) {
    const err = storableError(error);
    dispatch({ type: MARK_NOTIFICATIONS_SEEN_ERROR, payload: err });
    throw err;
  }
};

// ================ Selectors ================ //

export const getCrewContent = state => state.userGeneratedContent.crewContent || [];
export const getUserContent = state => state.userGeneratedContent.userContent || [];
export const getAllContent = state => state.userGeneratedContent.allContent || [];

export const isFetchingCrewContent = state => state.userGeneratedContent.isFetchingCrewContent;
export const isFetchingUserContent = state => state.userGeneratedContent.isFetchingUserContent;
export const isFetchingAllContent = state => state.userGeneratedContent.isFetchingAllContent;

export const isCreatingContent = state => state.userGeneratedContent.isCreatingContent;
export const getCreateContentError = state => state.userGeneratedContent.createContentError;
export const getAllContentError = state => state.userGeneratedContent.fetchAllContentError;
export const getCrewContentError = state => state.userGeneratedContent.fetchCrewContentError;
export const getUserContentError = state => state.userGeneratedContent.fetchUserContentError;
export const getHiddenPostIds = state => state.userGeneratedContent.hiddenPostIds || [];
export const getMutedUserIds = state => state.userGeneratedContent.mutedUserIds || [];
export const getBlockedUserIds = state => state.userGeneratedContent.blockedUserIds || [];
export const isReportingPost = state => state.userGeneratedContent.isReportingPost;
export const getReportPostError = state => state.userGeneratedContent.reportPostError;
export const getSocialNotificationUnseenCount = state =>
  countUnseenNotificationsExcludingTagged(state.userGeneratedContent.notifications || []);

export const isStreamFeedsInitialized = state => state.userGeneratedContent.streamFeedsInitialized;

// Legacy export for backward compatibility (components using old update pattern)
// This can be used for updating reactions/likes
export const updateContent = (activityId, action, data) => dispatch => {
  if (action === 'like') {
    return dispatch(addReaction(activityId, 'like'));
  } else if (action === 'unlike') {
    // For unlike, we need the reaction ID
    // This is a simplified version - proper implementation would track reaction IDs
    console.warn(
      'updateContent: Unlike not fully supported in legacy mode. Use removeReaction directly.'
    );
    return Promise.resolve();
  }

  // Other actions not supported in Stream Feeds yet
  console.warn('updateContent: Action not supported:', action);
  return Promise.resolve();
};

// Formatted selectors (transform Stream activities to post format)
// These import and use transformers from userGeneratedContentData.js
// Import is done dynamically to avoid circular dependency
export const getFormattedCrewContent = state => {
  const activities = getCrewContent(state);
  // Return activities as-is, transformation happens in components via transformer utility
  return activities;
};

export const getFormattedUserContent = state => {
  const activities = getUserContent(state);
  return activities;
};

export const getFormattedAllContent = state => {
  const activities = getAllContent(state);
  return activities;
};
